import React, { useRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as debounce from 'lodash.debounce';
import QuickSearchResult from './QuickSearchResult';
import { uniqueId } from 'lodash';
import { translate } from '../../Services/translation';
import { addOverlay, removeOverlay } from '../../Actions/Overlay.action';
import {
  query,
  setSearchQuery,
  handleKeyDown,
  toggleInputActive,
} from '../../Actions/QuickSearch.action';
import QuickSearchInitialTab from './QuickSearch.InitialTab';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { QuickSearchModel } from '../../Types/QuickSearchModel';
import Icon from '../Icon';
import clsx from 'clsx';
import useShowBothPrices from '../../Hooks/useShowBothPrices';

const debouncedQuery = debounce(
  (dispatch, text) => dispatch(query(text)),
  200
);

const QuickSearch = (props: QuickSearchModel) => {
  const quickSearch = useTypedSelector((state) => state.quickSearch);
  const { query, result, showResult, activeTab, inputActive } =
    quickSearch;

  const [id] = useState(uniqueId());
  const searchContainer = useRef(null);
  const searchInput = useRef(null);
  const dispatch = useDispatch();
  const searchUrl =
    global['LitiumQuickSearchUrl'] +
    (query.length > 0 ? `?q=${query}` : '');
  const showBothPrices = useShowBothPrices();
  const { currentType: currentPriceToggleType } = useTypedSelector(
    (state) => state.priceToggle
  );

  useEffect(() => {
    if (inputActive) {
      dispatch(
        addOverlay('quick-search__' + id, () =>
          dispatch(toggleInputActive(false))
        )
      );
    } else {
      dispatch(removeOverlay('quick-search__' + id));
    }
  }, [dispatch, inputActive, id]);

  return (
    <div
      className="flex w-full items-center lg:relative"
      role="search"
      ref={searchContainer}
    >
      <div className="relative flex w-full lg:w-4/5">
        <Icon
          name={inputActive ? 'close' : 'magnifier'}
          className={clsx(
            'absolute',
            'top-1/2',
            'translate-y-[-50%]',
            'right-3',
            'm-0',
            'text-xl',
            inputActive && 'text-gray-500',
            'w-5',
            'h-5'
          )}
          onClick={() =>
            dispatch(toggleInputActive(!inputActive, inputActive))
          }
        />
        <input
          className={clsx(
            'flex',
            'w-full',
            'grow',
            'px-3',
            'h-10',
            'font-normal',
            'leading-[150%]',
            'text-gray-900',
            'border',
            'border-solid',
            'search-decoration:hidden',
            inputActive ? 'border-gray-600' : 'border-gray-400',
            '[&:focus]:outline-none',
            'placeholder:text-gray-500'
          )}
          type="search"
          placeholder={translate('general.search')}
          autoComplete="off"
          value={decodeURIComponent(query)}
          onChange={(event) => {
            const text = encodeURIComponent(event.target.value);
            dispatch(setSearchQuery(text));
            debouncedQuery(dispatch, text);
          }}
          onClick={() => {
            dispatch(toggleInputActive(true));
          }}
          onKeyDown={(event) =>
            dispatch(handleKeyDown(event, { searchUrl }))
          }
          ref={searchInput}
        />
      </div>
      {showResult && inputActive && result && (
        <QuickSearchResult
          result={result}
          searchUrl={searchUrl}
          noHitDescription={props.noHitDescription}
          noHitHeader={props.noHitHeader}
          activeTab={activeTab}
          showBothPrices={showBothPrices}
          currentPriceToggleType={currentPriceToggleType}
        />
      )}

      {!showResult && inputActive && <QuickSearchInitialTab {...props} />}
    </div>
  );
};

export default QuickSearch;
